/**
 * Where a position in the web composer's document lands in the post's *text*.
 *
 * The web input is a rich editor (tiptap) while everything downstream — the
 * facets, the character count, and the ranges of a styled post — is measured in
 * the plain text that editor serializes to (`editorJsonToText` in
 * `TextInput.web.tsx`). The two count different things: a mention is one
 * `@handle`'s worth of characters rather than a node, a hard break is one line
 * feed rather than an item, and every paragraph but the last ends with a `\n`.
 * A selection reported by the editor is a pair of *document* positions, which
 * include the tokens those blocks and nodes occupy, so the two sets of numbers
 * do not line up — a highlight painted from an untranslated selection would start
 * one character early for every mention before it.
 *
 * This walks the document in the same order the serializer does and answers with
 * the text offset of a document position. Native needs none of it: a native
 * `TextInput` already reports its selection in the offsets of the string it holds.
 */
import {type Node as PMNode} from '@tiptap/pm/model'

/**
 * The text offset of `pos`, a position in `doc`.
 *
 * Clamped at both ends, and clamped *within* a leaf node: an atom has no
 * positions inside it, so a position that would fall within one is answered with
 * the end of what that atom says.
 */
export function textOffsetAt(doc: PMNode, pos: number): number {
  const target = Math.max(0, Math.min(pos, doc.content.size))
  /** The document position of the seam we are standing on. */
  let docPos = 0
  /** The number of characters the text has produced so far. */
  let offset = 0
  let found: number | undefined

  /**
   * `contentStart` is where this node's *content* begins, which is where its
   * children begin: the document's own content starts at position 0, and a block's
   * content starts one position past the block. Getting that one position wrong is
   * the difference between a working highlight and one that is a word out.
   */
  const walk = (node: PMNode, contentStart: number, isLastOfDoc: boolean) => {
    docPos = contentStart
    const end = contentStart + node.content.size + 1

    for (let i = 0; i < node.childCount; i++) {
      if (found !== undefined) break
      const child = node.child(i)
      const start = docPos
      if (target <= start) {
        found = offset
        break
      }
      const childEnd = start + child.nodeSize

      if (child.isText) {
        if (target < childEnd) {
          found = offset + (target - start)
          break
        }
        offset += child.nodeSize
        docPos = childEnd
        continue
      }

      if (child.isLeaf) {
        const produced = leafText(child)
        if (target < childEnd) {
          found = offset + produced.length
          break
        }
        offset += produced.length
        docPos = childEnd
        continue
      }

      /* A block inside the document — the composer's paragraphs. */
      walk(
        child,
        start + 1,
        node.type.name === 'doc' && i === node.childCount - 1,
      )
    }

    if (found === undefined && node.type.name === 'paragraph' && !isLastOfDoc) {
      offset += 1
    }
    docPos = end
  }

  walk(doc, 0, false)
  return found ?? offset
}

/**
 * The document position an offset of the post's own text sits at — the way back.
 *
 * Needed the moment the composer edits the words *underneath* the editor rather
 * than through it, which is what a Kashida is: the reducer inserts a Tatweel into
 * the post (`state/postStyle/typography`), the editor is handed the changed text,
 * and the author's selection has to land on the same words it was on.
 *
 * Written as a search against {@link textOffsetAt} rather than as a second walk of
 * its own: the two are inverses, and an inverse that is spelled out separately is
 * an inverse that can disagree with the function it claims to invert. The forward
 * reading is monotone — a position never maps to an earlier offset than one before
 * it — so the smallest position whose offset reaches the target *is* the answer,
 * and it maps back to exactly that offset by construction.
 */
export function docPosAtTextOffset(doc: PMNode, offset: number): number {
  const target = Math.max(0, offset)
  let low = 0
  let high = doc.content.size
  while (low < high) {
    const mid = Math.floor((low + high) / 2)
    if (textOffsetAt(doc, mid) < target) low = mid + 1
    else high = mid
  }
  return low
}

/**
 * A selection, moved by an edit that happened elsewhere.
 *
 * The edit is read as the difference between the text before it and the text
 * after: everything up to the first change is untouched, everything after the
 * last change keeps its distance (shifted by however many characters the edit
 * added or removed), and a selection that lay *inside* the changed stretch — which
 * a Kashida press can do, when the Tatweel goes in before the last letter of the
 * word being typed — is answered with the end of the replacement rather than with
 * a position that no longer means anything.
 */
export function adjustSelectionForEdit(
  before: string,
  after: string,
  selection: {start: number; end: number},
): {start: number; end: number} {
  const shortest = Math.min(before.length, after.length)
  let head = 0
  while (head < shortest && before[head] === after[head]) head++
  let tail = 0
  while (
    tail < shortest - head &&
    before[before.length - 1 - tail] === after[after.length - 1 - tail]
  ) {
    tail++
  }
  const removed = before.length - head - tail
  const inserted = after.length - head - tail

  const move = (position: number) => {
    if (position <= head) return position
    if (position >= head + removed) return position + inserted - removed
    return head + inserted
  }

  const start = Math.max(0, Math.min(move(selection.start), after.length))
  const end = Math.max(0, Math.min(move(selection.end), after.length))
  return {start, end}
}

/** What a leaf node says, as `editorJsonToText` writes it. */
function leafText(node: PMNode): string {
  if (node.type.name === 'hardBreak') return '\n'
  if (node.type.name === 'mention') return `@${node.attrs?.id ?? ''}`
  return ''
}
